<template>
  <div
    class="appointment-data-card-wrapper"
    :class="{
      'appointment-data-card-wrapper_error': isErrorState,
      'appointment-data-card-wrapper_primary': !isErrorState && hasPrimaryBorder,
    }"
  >
    <AppointmentDataCardSkeleton
      v-if="isSkeletonNeeded"
      v-show="isLoading"
      :data-qa="`${qaLocator}_skeleton`"
      :width="imageWidth"
      :height="withoutAvatar ? minHeight : imageHeight"
      :without-avatar="withoutAvatar"
    />
    <div
      v-show="!isLoading || !isSkeletonNeeded"
      class="appointment-data-card"
      :class="cardClass"
      :data-qa="qaLocator"
      :style="{ minHeight: `${minHeight}px` }"
    >
      <v-img
        v-if="imageSrc"
        class="appointment-data-card__image"
        :class="{ 'appointment-data-card__image_border': hasImageBorder }"
        :data-qa="`${qaLocator}_image`"
        :alt="name"
        :src="imageSrc"
        :width="imageWidth"
        :height="imageHeight"
        contain
      />
      <div
        v-else-if="iconStub"
        class="appointment-data-card__icon-stub-wrapper"
        :data-qa="`${qaLocator}_image_stub`"
      >
        <div
          class="ui-icon_type_40"
          :class="iconStub"
          :style="{
            width: `${imageWidth}px`,
            height: `${imageHeight}px`,
            lineHeight: `${imageHeight}px`,
            fontSize: `${iconStubSizeRatio * Math.min(imageHeight, imageWidth)}px`,
          }"
        />
      </div>
      <v-icon
        v-else-if="prependIcon"
        :class="isErrorState ? 'error--text' : 'icon-secondary--text'"
      >
        {{ prependIcon }}
      </v-icon>
      <div
        :class="[
          { 'ml-4': imageSrc || iconStub || prependIcon },
          $slots.icon ? 'mr-4' : 'mr-13',
        ]"
      >
        <template v-if="name">
          <div
            :class="[
              isBoldName ? 'text-subtitle-1' : 'text-body-1',
              isErrorState ? 'error--text' : 'text--text',
            ]"
            :data-qa="`${qaLocator}_name`"
          >
            {{ name }}
          </div>
          <div
            v-if="subtitle"
            class="text-body-2"
            :class="[
              isErrorState ? 'error--text' : 'text-secondary--text',
            ]"
            :data-qa="`${qaLocator}_subtitle`"
          >
            {{ subtitle }}
          </div>
          <div
            v-if="validationText"
            class="text-body-2 text-secondary--text"
            :data-qa="`${qaLocator}_validation`"
          >
            {{ validationText }}
          </div>
          <div
            v-for="({ text, qaLocatorName, classes }, index) in filteredDescriptions"
            :key="index"
            :data-qa="`${qaLocator}_${qaLocatorName || 'description'}`"
            :class="classes || 'text-body-2 text-secondary--text'"
          >
            {{ text }}
          </div>
        </template>
        <div
          v-else-if="titleStub"
          class="text-body-1 text-secondary--text"
        >
          {{ titleStub }}
        </div>
        <div
          v-if="warnDescription"
          class="text-body-2 text-warning--text"
          :data-qa="`${qaLocator}_warn_description`"
        >
          {{ warnDescription }}
        </div>
        <div
          v-if="errorDescription"
          class="text-body-2 text-error--text"
          :data-qa="`${qaLocator}_error_description`"
        >
          {{ errorDescription }}
        </div>
      </div>
      <div
        v-if="$slots.icon"
        class="appointment-data-card__icon"
        :class="isErrorState ? 'error--text' : 'icon-secondary--text'"
        :data-qa="`${qaLocator}_icon`"
      >
        <slot name="icon" />
      </div>
    </div>
    <slot />
  </div>
</template>

<script>
import AppointmentDataCardSkeleton from 'www/vue_components/common/AppointmentDataCard/components/AppointmentDataCardSkeleton.vue'

export default {
  name: 'AppointmentDataCard',
  components: {
    AppointmentDataCardSkeleton,
  },
  props: {
    descriptions: {
      type: Array,
      default: () => [{
        text: '',
        qaLocator: '',
      }],
    },
    hasImageBorder: {
      type: Boolean,
      default: false,
    },
    iconStub: {
      type: String,
      default: '',
    },
    imageSrc: {
      type: String,
      default: '',
    },
    imageWidth: {
      type: [String, Number],
      default: 80,
    },
    imageHeight: {
      type: [String, Number],
      default: 80,
    },
    isBoldName: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isSkeletonNeeded: {
      type: Boolean,
      default: false,
    },
    prependIcon: {
      type: String,
      default: '',
    },
    minHeight: {
      type: [String, Number],
      default: 40,
    },
    name: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
    warnDescription: {
      type: String,
      default: '',
    },
    errorDescription: {
      type: String,
      default: '',
    },
    withoutAvatar: {
      type: Boolean,
      default: false,
    },
    titleStub: {
      type: String,
      default: '',
    },
    qaLocator: {
      type: String,
      default: '',
    },
    isErrorState: {
      type: Boolean,
      default: false,
    },
    cardClass: {
      type: String,
      default: '',
    },
    validationText: {
      type: String,
      default: '',
    },
    hasPrimaryBorder: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    iconStubSizeRatio: 0.6,
  }),
  computed: {
    filteredDescriptions() {
      return this.descriptions.filter(({ text }) => !!text)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~www/themes/doctors/common/variables';

.appointment-data-card-wrapper {
  // Граница влияет на размеры элемента. Чтобы вёрстка не прыгала задаётся прозрачная граница
  border: 1px solid transparent;
  overflow: auto;

  &_error {
    border-radius: $border-radius-md;
    border-color: $ui-kit-error;
  }

  &_primary {
    border-radius: $border-radius-md;
    border-color: $ui-kit-primary;
  }
}

.appointment-data-card {
  display: flex;
  align-items: center;

  &__image {
    flex-grow: 0;

    &_border {
      border: 1px solid $ui-kit-bg-gray-60;
      border-radius: 4px;
    }
  }

  &__icon {
    color: $ui-kit-primary;
    padding-right: 12px;
    margin-left: auto;
  }

  &__icon-stub-wrapper {
    border-radius: 12px;
    background-color: $ui-kit-bg-gray-60;
  }
}
</style>
